/**
 * secret-admin-rpc-client
 *
 * The admin Secrets page's RPCs on the "db" mailbox: secret.v1.Types / List / Put /
 * Delete. ContinuumAdmin only — the server checks every call.
 *
 * A secret has a type (what it is, where one comes from, who may use it, how it is
 * secured), a name, and a label saying which one it is and where it came from. A value
 * goes up once (Put), sealed to the mesh's key so the request carries no value in the
 * clear, and never comes back: the server stores it encrypted, and no response carries a
 * stored value.
 */

import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";
import { sealP256 } from "./sealed";

/** One type of secret: the lineage every secret of that type shares. */
export interface SecretType {
  kind: string;
  /** the type a plugin's configuration field is written with, "" when none */
  checkpointType: string;
  what: string;
  origin: string;
  usedBy: string;
  securedBy: string;
  /** a secret of this type has a name; other types belong to one row and are not listed */
  named: boolean;
}

/** A named secret, without its value. */
export interface Secret {
  kind: string;
  name: string;
  /** which one this is */
  description: string;
  /** where this one came from */
  source: string;
  /** the hosts its value may be sent to (its security settings) */
  hosts: string[];
  enteredBy: string;
  enteredAt: string;
  rotatedAt: string;
  /** what names this secret today */
  usedBy: string[];
}

export interface PutSecretInput {
  kind: string;
  name: string;
  /** "" keeps the stored value and changes the label and hosts only */
  value: string;
  description: string;
  source: string;
  /** empty takes the type's default for the value */
  hosts: string[];
}

interface RawSecret {
  kind?: string;
  name?: string;
  description?: string;
  source?: string;
  security?: string;
  enteredBy?: string;
  enteredAt?: string;
  rotatedAt?: string;
  usedBy?: string[];
}

/** "a.example, b.example" as the server takes hosts; blanks dropped. */
export function parseHosts(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((h) => h.trim())
    .filter((h) => h.length > 0);
}

/** The security settings a list of hosts stands for; "" when there are none, which takes the type's default. */
export function securityFor(hosts: string[]): string {
  return hosts.length === 0 ? "" : JSON.stringify({ hosts });
}

function hostsOf(security: string | undefined): string[] {
  try {
    const parsed = JSON.parse(security || "{}") as { hosts?: unknown };
    return Array.isArray(parsed.hosts) ? parsed.hosts.map(String) : [];
  } catch {
    return [];
  }
}

export function toSecret(raw: RawSecret | undefined): Secret | undefined {
  if (!raw) return undefined;
  return {
    kind: raw.kind ?? "",
    name: raw.name ?? "",
    description: raw.description ?? "",
    source: raw.source ?? "",
    hosts: hostsOf(raw.security),
    enteredBy: raw.enteredBy ?? "",
    enteredAt: raw.enteredAt ?? "",
    rotatedAt: raw.rotatedAt ?? "",
    usedBy: raw.usedBy ?? [],
  };
}

let targetMailbox: MailboxAddress | null = null;

async function rpcCall<TResp>(endPoint: string, request: Record<string, unknown>): Promise<TResp> {
  if (!targetMailbox) {
    targetMailbox = await nameMappingService.resolve(asLogicalMailbox("db"));
  }
  const rpcResponse = await GlobalClient.get().rawRpcCall({
    to: targetMailbox,
    endPoint,
    body: new TextEncoder().encode(JSON.stringify(request)),
    contentType: ContentType.Json,
  });
  if (rpcResponse.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
    const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
    throw new Error(
      errorInfo?.message || errorInfo?.errorNode?.frame?.message || `RPC call to ${endPoint} failed`,
    );
  }
  if (!rpcResponse.data || rpcResponse.data.length === 0) {
    return {} as TResp;
  }
  return JSON.parse(new TextDecoder().decode(rpcResponse.data)) as TResp;
}

export async function listSecretTypes(): Promise<SecretType[]> {
  const resp = await rpcCall<{ types?: Partial<SecretType>[] }>("secret.v1.Types", {});
  return (resp.types ?? []).map((t) => ({
    kind: t.kind ?? "",
    checkpointType: t.checkpointType ?? "",
    what: t.what ?? "",
    origin: t.origin ?? "",
    usedBy: t.usedBy ?? "",
    securedBy: t.securedBy ?? "",
    named: t.named ?? false,
  }));
}

/** Every named secret, or those of one type. */
export async function listSecrets(kind = ""): Promise<Secret[]> {
  const resp = await rpcCall<{ secrets?: RawSecret[]; error?: string }>("secret.v1.List", { kind });
  if (resp.error) throw new Error(resp.error);
  return (resp.secrets ?? []).map((s) => toSecret(s) as Secret);
}

/** What a value put as kind/name is sealed for: the server opens it for that secret only. */
export function sealingContext(kind: string, name: string): string {
  return `${kind}/${name}`;
}

export async function putSecret(input: PutSecretInput): Promise<{ success: boolean; error: string; secret?: Secret }> {
  let value = "";
  if (input.value !== "") {
    const types = await rpcCall<{ sealingKey?: string }>("secret.v1.Types", {});
    if (!types.sealingKey) {
      return {
        success: false,
        error: "The mesh published no key to seal the value to: it is older than this page, or its config has no key to store secrets with.",
      };
    }
    value = await sealP256(types.sealingKey, sealingContext(input.kind, input.name), input.value);
  }
  const resp = await rpcCall<{ secret?: RawSecret; error?: string }>("secret.v1.Put", {
    kind: input.kind,
    name: input.name,
    value,
    description: input.description,
    source: input.source,
    security: securityFor(input.hosts),
  });
  return { success: !resp.error && Boolean(resp.secret), error: resp.error ?? "", secret: toSecret(resp.secret) };
}

export async function deleteSecret(kind: string, name: string): Promise<{ success: boolean; error: string }> {
  const resp = await rpcCall<{ error?: string }>("secret.v1.Delete", { kind, name });
  return { success: !resp.error, error: resp.error ?? "" };
}
